/**
 * The `Tiles` catalog component: one to three named figures, large.
 *
 * The only component the automatic view never reaches. Which three figures
 * an answer is about is a statement, and rows cannot make it — a
 * fundamentals envelope carries a dozen — so the model names them in a
 * directive and the server fills them. What the model chose is WHICH
 * figures; everything in the tile is the server's, including the tone.
 *
 * THE TONE IS NOT A CHOICE MADE HERE. It arrives on the wire, derived from
 * the figure's own sign, and this file only knows which token each tone
 * maps to. Green and red belong to the market; `mitra` is amber and is
 * unreachable from a row, so it appears in the map for completeness and a
 * tile will never carry it.
 *
 * The label is the tool's own field name with its underscores out. Prettier
 * names would have to be invented somewhere, and inventing them on the
 * client is how two surfaces end up calling one figure two things.
 */
import { z } from 'zod/v3'
import type { ComponentApi } from '@a2ui/web_core/v0_9'
import { createComponentImplementation } from '@a2ui/react/v0_9'
import type { CellValue } from '@/features/mitra/lib/figures'
import { captionLabel, humanLabel } from '@/features/mitra/lib/figures'
import { useLabelLang } from '@/features/mitra/panel/blocks/charts/labelLang'
import { Fig, Label, SCROLL_X } from '@/features/mitra/panel/blocks/Card'
import { PathBinding } from '@/features/mitra/panel/blocks/charts/Line'

export const TilesApi = {
  name: 'Tiles',
  schema: z
    .object({
      tiles: PathBinding,
      caption: PathBinding.optional(),
    })
    .strict(),
}

/** The shape behind `/tiles`, fixed by `to_messages`. */
export type TilePart = {
  id: string
  label: string
  value: CellValue
  tone: string
}

/** Tone to token. Never a hex: the dark theme redefines these, not this file. */
const TONE: Record<string, string> = {
  market_up: 'text-up',
  market_down: 'text-down',
  mitra: 'text-caution',
  plain: 'text-ink',
}

function listOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

// Crosses the zod/v3 <-> @a2ui/web_core-zod boundary once — see the comment
// on `Line`'s own `createComponentImplementation` call for why this is a
// named `const` and not an inline cast: eslint's type-aware rules crash on
// the inline form the same way tsc crashes on `PathBinding`'s union.
const tilesApi = TilesApi as unknown as ComponentApi

export const Tiles = createComponentImplementation<ComponentApi>(
  tilesApi,
  ({ props }: { props: { tiles?: unknown; caption?: unknown } }) => (
    <TileRow
      tiles={listOf<TilePart>(props.tiles)}
      caption={typeof props.caption === 'string' ? props.caption : null}
    />
  ),
)

/** The drawing, given the list the surface resolved. */
function TileRow({ tiles, caption }: { tiles: TilePart[]; caption: string | null }) {
  const lang = useLabelLang()
  if (tiles.length === 0) {
    return <div data-role="chart-empty" className="px-3 py-2 text-body-xs text-ink-soft" />
  }
  return (
    <figure
      data-role="chart"
      data-component="tiles"
      className="m-0 flex flex-col gap-2 px-3 pb-2 pt-2"
    >
      {caption && (
        <figcaption data-role="caption">
          <Label>{captionLabel(caption)}</Label>
        </figcaption>
      )}
      <div className="flex flex-wrap gap-2">
        {tiles.map((t) => (
          <div
            key={t.id}
            data-role="tile"
            data-tone={t.tone}
            // FILLED, NOT OUTLINED, and the design's own three-up (13l) is
            // why: three bordered boxes at this width read as a table with
            // the rules missing. `bg-ground` is the same fill the withheld
            // row and the chips sit on.
            className="flex min-w-38 flex-1 flex-col gap-2 rounded-block
                       bg-ground px-3 py-3"
          >
            {/* WRAPS rather than truncating. A label is the tile's name and
                a name cut to "DIVIDEND YI…" names nothing; the design lets
                both the label and the figure run to a second line. */}
            <Label className="leading-tight">{humanLabel(t.label, lang)}</Label>
            {/* A larger figure AND A SCROLLER, which is this card's own answer to a
                figure that will not fit (`Wide` in Card.tsx: "a figure too
                wide for its cell scrolls; it never clips"). Without one, at 19px with
                nowrap, -1,200.00 drew straight through the card's edge; the
                obvious fix — let it wrap — is worse, because it broke the
                number as "-1,200.0 / 0" and a number split across two lines
                reads as two numbers. Neither clipped nor broken: scrolled. */}
            <span className={`block ${SCROLL_X}`}>
              <Fig
                cell={t.value}
                quietUnit
                className={`text-mono-l font-medium leading-tight
                            ${TONE[t.tone] ?? TONE.plain ?? ''}`}
              />
            </span>
          </div>
        ))}
      </div>
    </figure>
  )
}
